import { fromJSON } from "@suse-o11y/from-json";
import { isString } from "@suse-o11y/is-string";
import { toJSON } from "@suse-o11y/to-json";
import React from "react";

type FirstParameter<TFunc> = TFunc extends (arg: infer TArg, ...args: Array<unknown>) => unknown ? TArg : never;

export interface AssignNodeFieldProps<
	TInstance,
	TMethod extends keyof TInstance,
	TValue extends FirstParameter<TInstance[TMethod]>,
> {
	instance: TInstance;
	method: TMethod;
	value: TValue;
	serialize?: boolean;
}

export function AssignNodeField<
	TInstance,
	TMethod extends keyof TInstance,
	TValue extends FirstParameter<TInstance[TMethod]>,
>({ instance, method, value, serialize = false }: AssignNodeFieldProps<TInstance, TMethod, TValue>): React.ReactNode {
	const serialized = serialize ? toJSON(value) : value;

	React.useLayoutEffect(() => {
		const deserialized = serialize && isString(serialized) ? deserialize(serialized) : serialized;

		(instance[method] as unknown as undefined | React.Dispatch<unknown>)?.(deserialized);
	}, [instance, method, serialize, serialized]);

	// unset the value on unmount (happens when value changes to undefined)
	React.useLayoutEffect(() => {
		return () => {
			// undefined is valid value for Konva properties for setting the default value
			(instance[method] as unknown as undefined | React.Dispatch<unknown>)?.(undefined);
		};
	}, [instance, method]);

	return null;
}

function deserialize(input: string) {
	const [, output] = fromJSON(input);

	return output?.unsafe;
}
